import { useT } from '../i18n/locale-context';
import { countryConfig } from '../country';
import { StatTile } from './charts/stat-tile';

/** Mirrors `SettlementDelta` on the API side. */
export interface SettlementDeltaView {
  sample: number;
  offerUzs: number;
  contractUzs: number;
  /** Signed: positive = settled below the offer, negative = settled above it. */
  discountPct: number;
  belowCount: number;
  belowPct: number;
}

/**
 * How far this party's contracts settle against the winning offer.
 *
 * We hold the winning OFFER; DXMAP holds the SIGNED CONTRACT. The gap is a fact
 * neither source has alone.
 *
 * Three deliberate choices:
 *
 * 1. **Renders nothing below the sample floor.** The API sends null there. A
 *    dash or a zero would invite the reader to infer that contracts settle at
 *    the offer — a different claim from "we do not know".
 * 2. **The sign picks the LABEL, not the number.** Buyer-level deltas run
 *    −42.9% to +82.4%; a contract above the offer is a real outcome, and
 *    printing "−12%" beneath a "settled below" heading would be false. The
 *    magnitude is shown under whichever label is true.
 * 3. **The row share sits beside the percentage.** The percentage is
 *    value-weighted, so one large contract can carry a whole profile. Of the
 *    466 buyers that clear the sample, 36% settle at essentially zero and 16%
 *    at 10%+ — the share is what separates a pattern from an outlier.
 *
 * The `xarid.imv.uz` link is a licence condition on DXMAP-derived data, not
 * decoration. DXMAP is Uzbekistan's aggregator: elsewhere (Kazakhstan, whose
 * `buyer.settlement.source` already reads «открытые данные о договорах») the
 * line stays as plain text with no link to an Uzbek site (audit 2026-10-16).
 */
const SETTLEMENT_SOURCE_URL: string | null =
  countryConfig.code === 'uz' ? 'https://xarid.imv.uz' : null;

export function SettlementTile({ settlement }: { settlement: SettlementDeltaView | null }) {
  const t = useT();
  if (!settlement) return null;

  const below = settlement.discountPct >= 0;
  const magnitude = Math.abs(settlement.discountPct).toFixed(1);

  return (
    <div>
      <StatTile
        label={below ? t('buyer.settlement.below') : t('buyer.settlement.above')}
        value={`${magnitude}%`}
        sub={t('buyer.settlement.sub', {
          n: settlement.sample,
          pct: settlement.belowPct.toFixed(0),
        })}
      />
      <p className="mt-1 text-[11px] text-ink-muted">
        {SETTLEMENT_SOURCE_URL ? (
          <a
            href={SETTLEMENT_SOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
          >
            {t('buyer.settlement.source')}
          </a>
        ) : (
          t('buyer.settlement.source')
        )}
      </p>
    </div>
  );
}
